/**
 * GuidesTab — the "User Guides" tab body on /help (HelpPage).
 */

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Col, Input, List, Row, Space, Tag, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { EmptyState } from "../../../components/shared";
import { articles, categoryDefs } from "../../../content/help";
import { visibleArticles, buildCategories, searchArticles } from "../../../content/help/model";
import { isPatientPortalUser } from "../../../utils/userPrefs/validatePermissions";

const { Text, Title } = Typography;

const GuidesTab: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const viewerIsPatient = isPatientPortalUser();
  const visible = useMemo(() => visibleArticles(articles, viewerIsPatient), [viewerIsPatient]);
  const categories = useMemo(() => buildCategories(visible, categoryDefs), [visible]);
  const results = useMemo(() => searchArticles(visible, query), [visible, query]);
  const searching = query.trim().length > 0;

  const categoryTitle = (slug: string) => categoryDefs.find((c) => c.slug === slug)?.title ?? slug;

  return (
    <div data-testid="help-guides-tab" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: "var(--mg-text-secondary)" }} />}
        placeholder="Search guides"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ maxWidth: 360 }}
        data-testid="help-guides-search"
      />

      {searching ? (
        results.length === 0 ? (
          <EmptyState title="No guides match your search" description="Try a different term." />
        ) : (
          <List
            itemLayout="vertical"
            dataSource={results}
            renderItem={(article) => (
              <List.Item key={article.path} style={{ paddingLeft: 0, paddingRight: 0 }}>
                <Space size={8} wrap>
                  <Link
                    to={article.path}
                    data-testid={`help-guides-article-link-${article.slug}`}
                    style={{ fontSize: 14, fontWeight: 600 }}
                  >
                    {article.title}
                  </Link>
                  <Tag>{categoryTitle(article.category)}</Tag>
                </Space>
                <div style={{ marginTop: 2 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {article.summary}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        )
      ) : (
        <Row gutter={[16, 16]}>
          {categories.map((category) => (
            <Col key={category.slug} xs={24} sm={12} xl={8}>
              <Card
                hoverable
                onClick={() => navigate(`/help/guides/${category.slug}`)}
                data-testid={`help-guides-category-${category.slug}`}
                styles={{ body: { padding: 16 } }}
              >
                <Title level={5} style={{ margin: 0, fontSize: 14 }}>
                  {category.title}
                </Title>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4, minHeight: 36 }}>
                  {category.summary}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
                  {category.articles.length} {category.articles.length === 1 ? "guide" : "guides"}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {!searching && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {visible.length} {visible.length === 1 ? "guide" : "guides"} available
        </Text>
      )}
    </div>
  );
};

export default GuidesTab;
